/**
 * PATH: packages/ui/src/kanban/utils/cardSelection.ts
 * FILE_ID: UI-KANBAN-CARD-SELECTION-001
 * PURPOSE: Pure helpers for board-level multi-select of cards and the
 *   optimistic reparent (nest multiple cards under one parent card).
 * WHY: Operator ask 2026-06-15: "Allow multiselecting cards in Kanban and
 *   drag them all under a sub-tended card ... when I drag a card it should
 *   instantly subtend, it didn't until I refreshed." The board owns the
 *   selection Set (so it spans lanes); drag serialises the set into a MIME;
 *   the drop reparents every selected card. Keeping the logic here makes
 *   it unit-testable in isolation and keeps KanbanCard / KanbanLane /
 *   KanbanBoardView thin.
 * FLOW:
 *   click(modifier) -> nextSelection -> Set<id>
 *   dragstart       -> dragCardIds -> string[] (stamped under MULTI_CARD_MIME)
 *   drop            -> parseDragCardIds -> planReparent -> reparentLaneCards
 * DEPENDENCIES:
 *   - ../types (Card): reads id / lane / parent_card / child_count.
 * RELATED:
 *   - components/KanbanCard.tsx: selection ring + modifier-click.
 *   - components/KanbanLane.tsx: serialises the drag payload.
 *   - components/KanbanBoardView.tsx: owns the Set + optimistic nest.
 */
import type { Card } from '../types'

/** MIME stamped on a drag when more than one card is selected. The
 *  single-card MIME (`text/x-kanban-card-id`) is ALWAYS stamped too so
 *  every existing single-drag drop target keeps working unchanged. */
export const MULTI_CARD_MIME = 'text/x-kanban-card-ids'

/** A single optimistic reparent: move card `id` from `fromLane` into the
 *  parent's lane (`toLane`) and set its `parent_card`. */
export interface ReparentMove {
  id: string
  fromLane: string
  toLane: string
}

/**
 * nextSelection — toggle `id` in the selection.
 *
 * `additive` true (modifier-click) toggles membership while preserving
 * the rest of the set. `additive` false starts a fresh single-item
 * selection (kept for completeness; the board only calls this additively).
 */
export function nextSelection(
  prev: ReadonlySet<string>,
  id: string,
  additive: boolean,
): Set<string> {
  const next = new Set<string>(additive ? prev : [])
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

/**
 * selectIdRange — union a contiguous range into the selection.
 *
 * Implements Finder/Trello shift-click semantics: holding Shift selects
 * every card from the last-clicked anchor through the shift-clicked
 * target (inclusive), in the lane's visual order, ADDED to whatever was
 * already selected. Range is computed over `orderedIds` (the lane's
 * depth-first ordered card ids).
 *
 * Defensive: when there is no anchor, or the anchor/target aren't in the
 * ordered list, it degrades to "just add the target" so a shift-click is
 * never a no-op. Pure — never mutates `prev`.
 */
export function selectIdRange(
  prev: ReadonlySet<string>,
  orderedIds: readonly string[],
  anchorId: string | null,
  targetId: string,
): Set<string> {
  const next = new Set<string>(prev)
  const targetIdx = orderedIds.indexOf(targetId)
  if (targetIdx < 0) {
    next.add(targetId)
    return next
  }
  const anchorIdx = anchorId != null ? orderedIds.indexOf(anchorId) : -1
  if (anchorIdx < 0) {
    next.add(targetId)
    return next
  }
  const lo = Math.min(anchorIdx, targetIdx)
  const hi = Math.max(anchorIdx, targetIdx)
  for (let i = lo; i <= hi; i++) next.add(orderedIds[i])
  return next
}

/**
 * dragCardIds — the ids a drag should carry.
 *
 * Dragging a card that is part of a multi-selection carries the WHOLE
 * selection; dragging any other card (or with a 0/1-item selection)
 * carries just that one card. This matches Finder / Trello semantics:
 * grabbing an unselected item drags only it.
 */
export function dragCardIds(
  selected: ReadonlySet<string>,
  draggedId: string,
): string[] {
  if (selected.size > 1 && selected.has(draggedId)) {
    return Array.from(selected)
  }
  return [draggedId]
}

/**
 * parseDragCardIds — read the dragged-id list back at drop time.
 *
 * Prefers the JSON array stamped under `MULTI_CARD_MIME`; falls back to
 * the single-card id when the multi MIME is absent or malformed, so a
 * normal single-card drag (and older drag sources) still resolve to one
 * id. Defensive: never throws on a corrupt payload.
 */
export function parseDragCardIds(
  raw: string | null | undefined,
  fallbackId: string,
): string[] {
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        const ids = parsed.filter(
          (x): x is string => typeof x === 'string' && x.length > 0,
        )
        if (ids.length > 0) return ids
      }
    } catch {
      // fall through to the single-id fallback
    }
  }
  return fallbackId ? [fallbackId] : []
}

/**
 * planReparent — resolve which dragged cards actually need to move under
 * `parent`. Skips: the parent itself, duplicates, unknown ids, and any
 * card already nested directly under the parent in the parent's lane
 * (no-op). The BE re-checks same-board + depth-cap + cycle invariants;
 * this is just the optimistic plan.
 */
export function planReparent(
  cards: Card[],
  draggedIds: string[],
  parent: Card,
): ReparentMove[] {
  const byId = new Map(cards.map((c) => [c.id, c]))
  const toLane = parent.lane
  const seen = new Set<string>()
  const moves: ReparentMove[] = []
  for (const id of draggedIds) {
    if (!id || id === parent.id || seen.has(id)) continue
    seen.add(id)
    const c = byId.get(id)
    if (!c) continue
    if (c.parent_card === parent.id && c.lane === toLane) continue
    moves.push({ id, fromLane: c.lane, toLane })
  }
  return moves
}

/**
 * reparentLaneCards — apply the optimistic reparent to the per-lane card
 * arrays. Returns a NEW record containing only the touched lanes (the
 * caller writes each back into its React-Query cache). Pure: never
 * mutates the input arrays.
 *
 * Effects:
 *   - each moved card is removed from its `fromLane` array and appended
 *     to the parent's lane with `{ lane, parent_card }` updated;
 *   - the parent card's `child_count` is bumped by the number moved so
 *     the tile's sub-card chip updates instantly.
 */
export function reparentLaneCards(
  laneCards: Record<string, Card[]>,
  moves: ReparentMove[],
  parentId: string,
): Record<string, Card[]> {
  if (moves.length === 0) return {}
  const touched = new Set<string>()
  for (const m of moves) {
    touched.add(m.fromLane)
    touched.add(m.toLane)
  }
  const out: Record<string, Card[]> = {}
  for (const laneId of touched) out[laneId] = [...(laneCards[laneId] ?? [])]

  const movedById = new Map<string, Card>()
  for (const m of moves) {
    const fromArr = out[m.fromLane]
    const idx = fromArr.findIndex((c) => c.id === m.id)
    if (idx >= 0) {
      const [card] = fromArr.splice(idx, 1)
      movedById.set(m.id, { ...card, lane: m.toLane, parent_card: parentId })
    }
  }
  for (const m of moves) {
    const moved = movedById.get(m.id)
    if (!moved) continue
    const destArr = out[m.toLane]
    if (!destArr.some((c) => c.id === moved.id)) destArr.push(moved)
  }
  if (movedById.size > 0) {
    for (const laneId of touched) {
      out[laneId] = out[laneId].map((c) =>
        c.id === parentId
          ? { ...c, child_count: (c.child_count ?? 0) + movedById.size }
          : c,
      )
    }
  }
  return out
}
